// Integration surface for the Day 1–3 audio + intake modules. One
// hook wires the AudioSessionManager singleton, TTS pipeline, mic
// pipeline, conversation state machine, and IntakeRepository together
// so consuming components don't have to know about any of them.
//
// Day 4 deliverable. Day 5 wires this into Intake.jsx, replacing the
// old useVoice.js / elevenlabs.js / persistent-audio-element path.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  audioSessionManager,
  TTSPlayer,
  TTSSource,
  MicRecorder,
  TranscriptionSource,
  VoiceTelemetry,
  VoiceConversationStateMachine,
} from '../services/audio/index.js';
import { IntakeRepository } from '../services/intake/index.js';

// Mirrors api/speak.js's VOICE_IDS.female fallback — same voice the
// spike + AudioModulesTest page already validate against. No
// per-guide gender resolution here; consumers can override via the
// voiceId prop.
const DEFAULT_VOICE_ID = 'ys3XeJJA4ArWMhRpcX1D';

export default function useVoiceConversation({
  userId = null,
  authToken = null,
  sessionId = null,
  voiceId = DEFAULT_VOICE_ID,
  onTurnComplete,
  onError,
} = {}) {
  const [state, setState] = useState('idle');
  const [currentTranscript, setCurrentTranscript] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [messages, setMessages] = useState([]);

  const smRef = useRef(null);
  const repoRef = useRef(null);
  const telemetryRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const voiceIdRef = useRef(voiceId);
  const onTurnCompleteRef = useRef(onTurnComplete);
  const onErrorRef = useRef(onError);
  const setupRanRef = useRef(false);

  // Mirror prop updates into refs so the one-time setup effect doesn't
  // need to retrigger when these change.
  useEffect(() => {
    onTurnCompleteRef.current = onTurnComplete;
  }, [onTurnComplete]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    voiceIdRef.current = voiceId;
  }, [voiceId]);

  // One-time setup. StrictMode invokes effects twice in dev — the ref
  // guard prevents double-instantiation of state machine + repo.
  useEffect(() => {
    if (setupRanRef.current) return;
    setupRanRef.current = true;

    const telemetry = new VoiceTelemetry();
    if (userId) telemetry.setUserContext({ userId });
    telemetryRef.current = telemetry;

    const repo = new IntakeRepository({ telemetry });
    repoRef.current = repo;

    const sm = new VoiceConversationStateMachine({
      audioSessionManager,
      ttsPlayer: new TTSPlayer(audioSessionManager),
      ttsSource: new TTSSource(),
      micRecorder: new MicRecorder(audioSessionManager),
      transcriptionSource: new TranscriptionSource(),
      telemetry,
    });
    smRef.current = sm;

    sm.on('stateChange', ({ to }) => setState(to));
    sm.on('transcribeEnd', ({ text }) => setCurrentTranscript(text));
    sm.on('turnComplete', ({ transcript }) => {
      const sid =
        sessionIdRef.current ||
        (sessionIdRef.current = repo.startSession());
      telemetry.setSessionContext({ sessionId: sid });
      repo
        .saveTurn({
          session_id: sid,
          role: 'user',
          content: transcript,
        })
        .then((record) => {
          if (record) setMessages((prev) => [...prev, record]);
        })
        .catch((e) => {
          setLastError(e);
          if (typeof onErrorRef.current === 'function') {
            onErrorRef.current(e);
          }
        });
      if (typeof onTurnCompleteRef.current === 'function') {
        onTurnCompleteRef.current(transcript);
      }
    });
    sm.on('error', ({ error }) => {
      setLastError(error);
      if (typeof onErrorRef.current === 'function') onErrorRef.current(error);
    });

    (async () => {
      try {
        await repo.init();
        if (authToken) repo.setAuthToken(authToken);
        if (sessionIdRef.current) {
          telemetry.setSessionContext({ sessionId: sessionIdRef.current });
          const loaded = await repo.resumeSession(sessionIdRef.current);
          if (Array.isArray(loaded) && loaded.length) setMessages(loaded);
        }
      } catch (e) {
        setLastError(e);
        if (typeof onErrorRef.current === 'function') onErrorRef.current(e);
      }
    })();

    return () => {
      // TODO(Day 7 polish): add explicit dispose() on
      // VoiceConversationStateMachine + IntakeRepository so the retry
      // queue interval stops and event listeners drop on unmount. For
      // now, the page lifetime bounds these and a refresh is the only
      // teardown path.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep auth token fresh — Clerk refreshes the JWT periodically.
  useEffect(() => {
    if (repoRef.current) repoRef.current.setAuthToken(authToken || null);
  }, [authToken]);

  const init = useCallback(async () => {
    await audioSessionManager.unlock();
    if (smRef.current) await smRef.current.init();
  }, []);

  const speak = useCallback(async (text) => {
    if (!smRef.current) {
      throw new Error('useVoiceConversation: state machine not initialized');
    }
    if (!repoRef.current) {
      throw new Error('useVoiceConversation: repository not initialized');
    }
    const sid =
      sessionIdRef.current ||
      (sessionIdRef.current = repoRef.current.startSession());
    if (telemetryRef.current) {
      telemetryRef.current.setSessionContext({ sessionId: sid });
    }
    try {
      const record = await repoRef.current.saveTurn({
        session_id: sid,
        role: 'guide',
        content: text,
      });
      if (record) setMessages((prev) => [...prev, record]);
    } catch (e) {
      // Don't block the spoken turn on a persistence failure — surface
      // it via onError so the UI can decide, then proceed.
      setLastError(e);
      if (typeof onErrorRef.current === 'function') onErrorRef.current(e);
    }
    return smRef.current.speak(text, voiceIdRef.current);
  }, []);

  const startListening = useCallback(async () => {
    if (!smRef.current) {
      throw new Error('useVoiceConversation: state machine not initialized');
    }
    return smRef.current.listen();
  }, []);

  const stopListening = useCallback(async () => {
    if (!smRef.current) {
      throw new Error('useVoiceConversation: state machine not initialized');
    }
    return smRef.current.stopListening();
  }, []);

  const reset = useCallback(() => {
    if (smRef.current) smRef.current.reset();
  }, []);

  const cancel = useCallback(() => {
    if (smRef.current) smRef.current.cancel();
  }, []);

  return {
    state,
    currentTranscript,
    lastError,
    messages,
    speak,
    startListening,
    stopListening,
    reset,
    cancel,
    init,
  };
}
